import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { Button } from "@wondermaps/ui";
import type { UseVisitorFeedbackReturn } from "../../hooks/visitor/useVisitorFeedback";
import { StarRow } from "../shared/StarRow";

type FeedbackBannerProps = Pick<
  UseVisitorFeedbackReturn,
  "feedbackPhase" | "rating" | "comment" | "setRating" | "setComment" | "submit" | "skip" | "dismiss"
>;

export function FeedbackBanner({
  feedbackPhase,
  rating,
  comment,
  setRating,
  setComment,
  submit,
  skip,
  dismiss,
}: FeedbackBannerProps) {
  const { t } = useTranslation("feedback");
  if (!feedbackPhase) return null;

  // Phase 3: Thank you
  if (feedbackPhase === "thanks") {
    return (
      <div
        className="fixed inset-x-0 z-[39] bg-background/95 backdrop-blur-sm border-t"
        style={{ bottom: "var(--bottom-nav-height, 0px)" }}
      >
        <div className="max-w-lg mx-auto px-4 py-4 text-center">
          <p className="text-sm font-medium text-foreground">{t("thanksFeedback")}</p>
        </div>
      </div>
    );
  }

  // Phase 2: Expanded form
  if (feedbackPhase === "form") {
    return (
      <div
        className="fixed inset-x-0 z-[39] bg-background/95 backdrop-blur-sm border-t"
        style={{ bottom: "var(--bottom-nav-height, 0px)" }}
      >
        <div className="max-w-lg mx-auto px-4 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <StarRow rating={rating} onRate={setRating} size="sm" />
            <Button size="sm" variant="ghost" onClick={dismiss} className="px-2">
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
            </Button>
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t("anyThoughts")}
            maxLength={500}
            rows={2}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
          />
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={skip}>
              {t("common:skip")}
            </Button>
            <Button size="sm" onClick={submit} disabled={rating === null}>
              {t("common:submit")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Phase 1: Banner with star rating
  return (
    <div
      className="fixed inset-x-0 z-[39] bg-background/95 backdrop-blur-sm border-t"
      style={{ bottom: "var(--bottom-nav-height, 0px)" }}
    >
      <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground shrink-0">{t("howWasVisit")}</p>
        <StarRow rating={null} onRate={setRating} />
        <Button size="sm" variant="ghost" onClick={dismiss} className="px-2 shrink-0">
          <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
